import { Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ReportQuery, ReportQueryService } from '../../../core/services/report-query';
import { AutocompleteOption } from '../autocomplete-select/autocomplete-select.component';

@Component({
  selector: 'app-rql-select',
  standalone: false,
  templateUrl: './rql-select.html',
  styleUrl: './rql-select.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RqlSelect),
    multi: true,
  }],
})
export class RqlSelect implements ControlValueAccessor, OnChanges {
  @Input({ required: true }) source = '';
  @Input({ required: true }) valueField = 'id';
  @Input({ required: true }) labelField = 'name';
  @Input() labelFallbackFields: string[] = [];
  @Input() searchField = '';
  @Input() where: Record<string, unknown> | null = null;
  @Input() multiple = false;
  @Input() placeholder = 'Search...';
  @Input() selectClass = 'form-select';
  @Input() maxOptions = 50;

  value: string | string[] = '';
  disabled = false;
  optionScope = '';

  readonly searchOptions = (query: string) => this.load(query, []);
  readonly preloadOptions = (values: string[]) => this.load('', values);

  private onChange: (value: string | string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private readonly reportQuery: ReportQueryService) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.optionScope = JSON.stringify({
      source: this.source,
      valueField: this.valueField,
      labelField: this.labelField,
      labelFallbackFields: this.labelFallbackFields,
      where: this.where,
    });
  }

  writeValue(value: unknown): void {
    if (this.multiple) {
      this.value = Array.isArray(value) ? value.map(String) : [];
      return;
    }
    this.value = typeof value === 'string' ? value : '';
  }

  registerOnChange(fn: (value: string | string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  valueChanged(value: string | string[]): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  private async load(search: string, values: string[]): Promise<AutocompleteOption[]> {
    if (!this.source || !this.valueField || !this.labelField) return [];

    const select = [
      { type: 'field', field: this.valueField, as: 'value' },
      { type: 'field', field: this.labelField, as: 'label' },
      ...this.labelFallbackFields.map((field, index) => ({
        type: 'field', field, as: `label_fallback_${index}`,
      })),
    ];
    const conditions: Array<Record<string, unknown>> = [];
    if (this.where) conditions.push(this.where);
    if (values.length > 0) {
      conditions.push({ type: 'condition', field: this.valueField, operator: 'in', value: values });
    } else if (search.trim()) {
      conditions.push({
        type: 'condition',
        field: this.searchField || this.labelField,
        operator: 'contains',
        value: search.trim(),
      });
    }

    const query: ReportQuery = {
      version: 1,
      source: this.source,
      select,
      where: conditions.length === 0
        ? undefined
        : conditions.length === 1
          ? conditions[0]
          : { type: 'group', operator: 'and', children: conditions },
      order_by: [{ ref: 'label', direction: 'asc' }],
      limit: values.length > 0 ? Math.max(values.length, this.maxOptions) : this.maxOptions,
      offset: 0,
    };
    const result = await firstValueFrom(this.reportQuery.execute(query));
    const options = result.rows
      .map((row) => {
        const value = String(row['value'] ?? '').trim();
        const candidates = [
          row['label'],
          ...this.labelFallbackFields.map((_field, index) => row[`label_fallback_${index}`]),
          value,
        ];
        const label = candidates.map((candidate) => String(candidate ?? '').trim()).find(Boolean) ?? value;
        return { value, label };
      })
      .filter((option) => option.value);
    return Array.from(new Map(options.map((option) => [option.value, option])).values());
  }

}
